import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  GraduationCap, Plus, Loader2, Clock, BookOpen, Trash2, 
  Search, Filter, Users, Shield, Briefcase, Heart, 
  Settings, Calendar, ChevronRight, Play, CheckCircle2,
  Award, TrendingUp, Target
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ['Onboarding', 'Sécurité', 'Technique', 'Management', 'Soft Skills', 'Autre'];

const categoryConfig = {
  'Onboarding': { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Users, borderColor: 'border-l-emerald-500' },
  'Sécurité': { color: 'bg-red-100 text-red-700 border-red-200', icon: Shield, borderColor: 'border-l-red-500' },
  'Technique': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Settings, borderColor: 'border-l-blue-500' },
  'Management': { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Briefcase, borderColor: 'border-l-purple-500' },
  'Soft Skills': { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Heart, borderColor: 'border-l-amber-500' },
  'Autre': { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: BookOpen, borderColor: 'border-l-slate-400' }
};

export default function TrainingPage() {
  const { isAdmin } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [form, setForm] = useState({ title: '', description: '', content: '', duration_hours: '', category: 'Technique' });

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/trainings`);
      setTrainings(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error('Veuillez ajouter un titre');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/trainings`, { ...form, duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null });
      toast.success('Formation créée');
      setDialogOpen(false);
      setForm({ title: '', description: '', content: '', duration_hours: '', category: 'Technique' });
      fetchTrainings();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      await axios.delete(`${API_URL}/api/trainings/${id}`);
      toast.success('Formation supprimée');
      setViewDialogOpen(false);
      fetchTrainings();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const stats = {
    total: trainings.length,
    technique: trainings.filter(t => t.category === 'Technique').length,
    securite: trainings.filter(t => t.category === 'Sécurité').length,
    management: trainings.filter(t => t.category === 'Management').length,
    totalHours: trainings.reduce((acc, t) => acc + (t.duration_hours || 0), 0)
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch = training.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (training.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || training.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Formations</h1>
          <p className="text-slate-500 mt-1">Catalogue des formations disponibles</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />Nouvelle formation
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <GraduationCap className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Formations totales</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.totalHours}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Heures de contenu</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setCategoryFilter('Technique')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Settings className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.technique}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Techniques</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setCategoryFilter('Sécurité')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.securite}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Sécurité</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une formation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Trainings Grid */}
      {filteredTrainings.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-1">Aucune formation trouvée</h3>
            <p className="text-slate-500">
              {searchQuery || categoryFilter !== 'all' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Créez votre première formation pour commencer'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTrainings.map((training) => {
            const config = categoryConfig[training.category] || categoryConfig['Autre'];
            const CategoryIcon = config.icon;
            return (
              <Card 
                key={training.id} 
                className={`border-l-4 ${config.borderColor} cursor-pointer hover:shadow-lg transition-all group`}
                onClick={() => { setSelectedTraining(training); setViewDialogOpen(true); }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-2 rounded-lg ${config.color.split(' ')[0]}`}>
                      <CategoryIcon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                    </div>
                    <Badge className={`${config.color} border`}>{training.category}</Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                    {training.title}
                  </h3>
                  
                  {training.description && (
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{training.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      {training.duration_hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {training.duration_hours}h
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(training.created_at), 'dd MMM', { locale: fr })}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Nouvelle formation
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre <span className="text-red-500">*</span></Label>
                <Input 
                  value={form.title} 
                  onChange={(e) => setForm({ ...form, title: e.target.value })} 
                  placeholder="Nom de la formation"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={form.description} 
                  onChange={(e) => setForm({ ...form, description: e.target.value })} 
                  placeholder="Brève description"
                />
              </div>
              <div className="space-y-2">
                <Label>Durée (heures)</Label>
                <Input 
                  type="number" 
                  step="0.5" 
                  value={form.duration_hours} 
                  onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} 
                  placeholder="Ex: 2.5"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contenu de la formation</Label>
              <Textarea 
                value={form.content} 
                onChange={(e) => setForm({ ...form, content: e.target.value })} 
                rows={8} 
                placeholder="Décrivez le contenu, les objectifs et le programme de la formation..." 
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer la formation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          {selectedTraining && (
            <>
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className={`${categoryConfig[selectedTraining.category]?.color || categoryConfig['Autre'].color} border mb-2`}>
                      {selectedTraining.category}
                    </Badge>
                    <DialogTitle className="text-2xl">{selectedTraining.title}</DialogTitle>
                  </div>
                </div>
                
                <div className="flex items-center gap-6 mt-4 pt-4 border-t">
                  {selectedTraining.duration_hours && (
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Durée</p>
                        <p className="font-semibold">{selectedTraining.duration_hours} heures</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Créée le</p>
                      <p className="font-semibold">{format(new Date(selectedTraining.created_at), 'dd MMMM yyyy', { locale: fr })}</p>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 mt-4">
                {selectedTraining.description && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-slate-500" />
                      Description
                    </h4>
                    <p className="text-slate-600">{selectedTraining.description}</p>
                  </div>
                )}
                
                {selectedTraining.content && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-blue-700 mb-3 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Contenu de la formation
                    </h4>
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap text-slate-700">{selectedTraining.content}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
                <div className="flex items-center justify-between w-full">
                  <div>
                    {isAdmin && (
                      <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(selectedTraining.id)}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                      Fermer
                    </Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Marquer comme terminée
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
